import React from 'react'
import PropTypes from 'prop-types'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { businessInfoPath, businessInfoUpdatePath, businessInfoCommodityPath, businessInfoServiceAreaPath } from 'components/routes/path'
import { AuthContext, USER_TYPES, USER_STATES, canEditSupplier, canReplyToReview } from 'components/common/auth/AuthContext'
import { FeatureContext } from 'components/FeatureContext'
import Sidebar from './Sidebar'
import OverviewSection from './OverviewSection'
import CommodityCodeSection from './CommodityCodeSection'
import ContractVehicleSection from './ContractVehicleSection'
import RecentPostsSection from './RecentPostsSection'
import ServiceAreaSection from './ServiceAreaSection'
import ReviewSection from './ReviewSection'
import CommodityCodesShape from './prop_shapes/commodity_codes'
import SupplierShape from './prop_shapes/supplier'
import ContractVehiclesShape from './prop_shapes/contract_vehicles'
import TiledListItemShape from './prop_shapes/tiledListItem'
import ServiceAreaShape from './prop_shapes/service_area'
import ReviewMetadata from './prop_shapes/reviewMetadata'
import UnclaimedCTABox from './UnclaimedCTABox'
import ReviewCreateButton from './ReviewCreateButton'
import { EditButtonLink } from '../supplier_shared/EditButtonLink'

const queryClient = new QueryClient()

const Base = ({
  commodityCodes,
  contractVehicles,
  featureFlags,
  procuratedPromisePath,
  recentPosts,
  recentPostsNextPage,
  reviewMetadata,
  serviceArea,
  supplier,
  userState,
  userTxVpts,
  userType,
  userIsGooglebot
}) => {
  const user = {
    canReplyToReview: canReplyToReview({ userType }, supplier),
    isOwner: canEditSupplier({ userType }, supplier),
    isTxVpts: userTxVpts,
    userState,
    userType,
    userIsGooglebot
  }

  const showForSupplierOwner = (element) => {
    if (user.isOwner) return element

    return null
  }

  const shouldShowUnclaimedCTABox = user.userType === USER_TYPES.UNAUTHED && !supplier.claimed
  const shouldShowReviewButton = user.userType === USER_TYPES.AGENCY_USER || user.userType === USER_TYPES.UNAUTHED
  const shouldShowRecentPosts = supplier.pro && (recentPosts?.length > 0 || user.isOwner)

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureContext.Provider value={featureFlags}>
        <AuthContext.Provider value={user}>
          <div className={`grid-container${user.userType === USER_TYPES.UNAUTHED ? ' unauthed-header-padding' : ''}`} id='grid-container'>
            <img src={supplier.coverUrl} className='pro-rounded' alt='supplier cover' />

            <div className='supplier-content'>
              <div className='grid-x grid-margin-x'>
                <div className='large-3 medium-2 hide-for-small-only cell'>
                  <Sidebar supplier={supplier} shouldShowRecentPosts={shouldShowRecentPosts} />
                </div>

                <div className='large-9 medium-10 small-12 cell sections'>
                  <OverviewSection
                    supplier={supplier}
                    editButton={showForSupplierOwner(<EditButtonLink to={businessInfoPath(supplier.slug)} label='Edit Profile' />)}
                  />

                  {shouldShowReviewButton &&
                    <ReviewCreateButton
                      className='hide-for-large pro-close-shadow expanded spaced'
                      supplierId={supplier.id}
                      text={<b>Write a review for this supplier <i className='fas fa-edit' /></b>}
                    />}

                  <ContractVehicleSection
                    contractVehicles={contractVehicles}
                    editButton={showForSupplierOwner(<EditButtonLink to={businessInfoPath(supplier.slug)} label='Add/Edit Contracts' />)}
                  />

                  {shouldShowRecentPosts &&
                    <RecentPostsSection
                      supplierId={supplier.id}
                      totalPostsCount={supplier.totalPostsCount}
                      recentPosts={recentPosts}
                      nextPage={recentPostsNextPage}
                      editButton={showForSupplierOwner(<EditButtonLink to={businessInfoUpdatePath(supplier.slug)} label='Add/Edit Posts' />)}
                    />}

                  <ServiceAreaSection
                    serviceArea={serviceArea}
                    editButton={
                      showForSupplierOwner(<EditButtonLink to={businessInfoServiceAreaPath(supplier.slug)} label='Add/Edit Service Areas' />)
                    }
                  />

                  <CommodityCodeSection
                    commodityCodes={commodityCodes}
                    editButton={
                      showForSupplierOwner(<EditButtonLink to={businessInfoCommodityPath(supplier.slug)} label='Add/Edit Commodities' />)
                    }
                  />

                  <ReviewSection
                    supplier={supplier}
                    metadata={reviewMetadata}
                    procuratedPromisePath={procuratedPromisePath}
                  />
                  {shouldShowUnclaimedCTABox && <UnclaimedCTABox className='hide-for-large' supplierId={supplier.id} />}
                </div>
              </div>
            </div>
          </div>
        </AuthContext.Provider>
      </FeatureContext.Provider>
    </QueryClientProvider>
  )
}

Base.propTypes = {
  commodityCodes: CommodityCodesShape,
  contractVehicles: ContractVehiclesShape,
  featureFlags: PropTypes.object,
  procuratedPromisePath: PropTypes.string.isRequired,
  recentPosts: PropTypes.arrayOf(TiledListItemShape),
  recentPostsNextPage: PropTypes.number,
  reviewMetadata: ReviewMetadata.isRequired,
  serviceArea: ServiceAreaShape,
  supplier: SupplierShape,
  userState: PropTypes.oneOf(Object.values(USER_STATES)),
  userTxVpts: PropTypes.bool,
  userType: PropTypes.oneOf(Object.values(USER_TYPES)),
  userIsGooglebot: PropTypes.bool
}

Base.defaultProps = {
  userState: USER_STATES.UNVERIFIED,
  userType: USER_TYPES.UNAUTHED,
  userIsGooglebot: false
}

export default Base
